import { Headline, Insight, HeadlineGroup, HeadlineGroupStat } from '@/types';
import { create } from 'zustand';

export interface UserProfile {
  email: string;
  name: string;
  picture: string;
  exp: number;
  sub: string;
  streams: Stream[];
  workspaces: Workspace[];
}

export interface DotItem {
  id: string | number;
  type: 'headline_group' | 'insight' | 'question';
  name: string;
}

export interface Stream {
  stream_id: number;
  stream_topic: string;
  stream_description: string;
}

export interface Workspace {
  workspace_id: number;
  workspace_name: string;
  workspace_description: string;
}

interface StoreState {
  headlineGroups: HeadlineGroup[];
  headlines: Headline[];
  insights: Insight[];
  headlineGroupStats: HeadlineGroupStat[];
  selectedHeadlineGroup: HeadlineGroup | null;
  editingText: string;
  error: Error | null;
  isLoading: boolean;
  userProfile: UserProfile | null;
  selectedDots: DotItem[];
  envoyPrompt: string;
  selectedStreamId: number | null;
  selectedWorkspaceId: number | null;
  streams: Stream[];
  workspaces: Workspace[];
  isStreamLoading: boolean;
  selectedTab: 'studio' | 'stream';
  selectedStreamIcon: string;
  selectedStudioIcon: string;
  expandedDates: Set<string>;
  expandedRows: Set<number>;
  
  setHeadlineGroups: (groups: HeadlineGroup[]) => void;
  setHeadlines: (headlines: Headline[]) => void;
  setInsights: (insights: Insight[]) => void;
  setHeadlineGroupStats: (stats: HeadlineGroupStat[]) => void;
  setSelectedHeadlineGroup: (group: HeadlineGroup | null) => void;
  setEditingText: (text: string) => void;
  setError: (error: Error | null) => void;
  setIsLoading: (loading: boolean) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  toggleDotSelection: (dot: DotItem) => void;
  clearDotSelections: () => void;
  setEnvoyPrompt: (prompt: string) => void;
  setSelectedStreamId: (id: number | null) => void;
  setSelectedWorkspaceId: (id: number | null) => void;
  setStreams: (streams: Stream[]) => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  setIsStreamLoading: (loading: boolean) => void;
  setSelectedTab: (tab: 'studio' | 'stream') => void;
  setSelectedStreamIcon: (icon: string) => void;
  setSelectedStudioIcon: (icon: string) => void;
  setSelectedIcon: (icon: string) => void;
  toggleExpandedDate: (date: string) => void;
  setExpandedDates: (dates: Set<string>) => void;
  toggleExpandedRow: (rowId: number) => void;
  setExpandedRows: (rows: Set<number>) => void;
}

export const useStore = create<StoreState>((set) => {
  console.log('Initializing store');
  return {
    headlineGroups: [],
    headlines: [],
    insights: [],
    headlineGroupStats: [],
    selectedHeadlineGroup: null,
    editingText: '',
    error: null,
    isLoading: false,
    userProfile: null,
    selectedDots: [],
    envoyPrompt: 'Generate an insight about these headlines',
    selectedStreamId: null,
    selectedWorkspaceId: null,
    streams: [],
    workspaces: [],
    isStreamLoading: false,
    selectedTab: 'stream',
    selectedStreamIcon: 'news',
    selectedStudioIcon: 'alerts',
    expandedDates: new Set<string>(),
    expandedRows: new Set<number>(),

    setHeadlineGroups: (groups: HeadlineGroup[]) => set({ headlineGroups: groups }),
    setHeadlines: (headlines: Headline[]) => set({ headlines }),
    setInsights: (insights: Insight[]) => set({ insights }),
    setHeadlineGroupStats: (stats: HeadlineGroupStat[]) => set({ headlineGroupStats: stats }),
    setSelectedHeadlineGroup: (group: HeadlineGroup | null) => set({ selectedHeadlineGroup: group }),
    setEditingText: (text: string) => set({ editingText: text }),
    setError: (error: Error | null) => set({ error }),
    setIsLoading: (loading: boolean) => set({ isLoading: loading }),
    setUserProfile: (profile: UserProfile | null) => set({ userProfile: profile }),
    toggleDotSelection: (dot: DotItem) => 
      set((state) => {
        const exists = state.selectedDots.some(
          d => d.id === dot.id && d.type === dot.type
        );
        
        if (exists) {
          return {
            selectedDots: state.selectedDots.filter(
              d => !(d.id === dot.id && d.type === dot.type)
            )
          };
        }
        
        return {
          selectedDots: [...state.selectedDots, dot]
        };
      }),

    clearDotSelections: () => set({ selectedDots: [] }),
    setEnvoyPrompt: (prompt: string) => set({ envoyPrompt: prompt }),
    setSelectedStreamId: (id: number | null) => {
      console.log('Setting selected stream ID:', id);
      set({ selectedStreamId: id });
    },
    setSelectedWorkspaceId: (id: number | null) => {
      console.log('Setting selected workspace ID:', id);
      set({ selectedWorkspaceId: id });
    },
    setStreams: (streams: Stream[]) => {
      console.log('Setting streams in store:', streams);
      set({ streams });
    },
    setWorkspaces: (workspaces: Workspace[]) => {
      console.log('Setting workspaces in store:', workspaces);
      set({ workspaces });
    },
    setIsStreamLoading: (loading: boolean) => set({ isStreamLoading: loading }),
    setSelectedTab: (tab: 'studio' | 'stream') => set({ selectedTab: tab }),
    setSelectedStreamIcon: (icon: string) => set({ selectedStreamIcon: icon }),
    setSelectedStudioIcon: (icon: string) => set({ selectedStudioIcon: icon }),
    setSelectedIcon: (icon: string) => 
      set((state) => {
        if (state.selectedTab === 'stream') {
          return { selectedStreamIcon: icon };
        } else {
          return { selectedStudioIcon: icon };
        }
      }),
    toggleExpandedDate: (date: string) => 
      set((state) => {
        const newExpandedDates = new Set(state.expandedDates);
        if (newExpandedDates.has(date)) {
          newExpandedDates.delete(date);
        } else {
          newExpandedDates.add(date);
        }
        return { expandedDates: newExpandedDates };
      }),
    setExpandedDates: (dates: Set<string>) => set({ expandedDates: dates }),
    toggleExpandedRow: (rowId: number) => 
      set((state) => {
        const newExpandedRows = new Set(state.expandedRows);
        if (newExpandedRows.has(rowId)) {
          newExpandedRows.delete(rowId);
        } else {
          newExpandedRows.add(rowId);
        }
        return { expandedRows: newExpandedRows };
      }),
    setExpandedRows: (rows: Set<number>) => set({ expandedRows: rows }),
  };
});

// Add a debug listener to log state changes
if (process.env.NODE_ENV === 'development') {
  useStore.subscribe((state, prevState) => {
    // Only log changes to streams, selectedStreamId, workspaces, selectedWorkspaceId, or icons
    if (
      state.streams !== prevState.streams ||
      state.selectedStreamId !== prevState.selectedStreamId ||
      state.workspaces !== prevState.workspaces ||
      state.selectedWorkspaceId !== prevState.selectedWorkspaceId ||
      state.selectedStreamIcon !== prevState.selectedStreamIcon ||
      state.selectedStudioIcon !== prevState.selectedStudioIcon
    ) {
      console.log('Store state changed:');
      console.log('- Streams:', state.streams);
      console.log('- Selected Stream ID:', state.selectedStreamId);
      console.log('- Selected Stream Icon:', state.selectedStreamIcon);
      console.log('- Workspaces:', state.workspaces);
      console.log('- Selected Workspace ID:', state.selectedWorkspaceId);
      console.log('- Selected Studio Icon:', state.selectedStudioIcon);
    }
  });
}